import { stage } from './stage';

/**
 * Agent invocation config — where the web console sends chat turns.
 *
 * All agents share a single endpoint (`POST {base}/agents`); the target agent
 * is selected by the `agentId` field in the request body. The endpoint is an
 * API Gateway custom domain that HTTP_PROXY-streams to the consolidated
 * AgentCore runtime container. Auth is a Cognito **access token** as a bearer
 * (see `src/agents/agentStream.ts`). The full contract lives in the sibling
 * agents repo's `docs/streaming.md`.
 */
export interface AgentConfig {
  /** Streaming-invoke URL, e.g. `https://api-beta.oculusmind.ai/agents`. */
  readonly invokeUrl: string;
}

// Public API Gateway origins (no secrets), safe to ship to the browser.
// `VITE_AGENTS_BASE_URL` overrides the origin at build time for CI.
const BETA_BASE = 'https://api-beta.oculusmind.ai';
const PROD_BASE = 'https://api.oculusmind.ai';

function buildConfig(s: 'local' | 'beta' | 'prod'): AgentConfig {
  // `prod-never-calls-beta`: prod resolves only the prod origin; the beta
  // default is never referenced from the prod branch.
  const base =
    s === 'prod'
      ? import.meta.env.VITE_AGENTS_BASE_URL || PROD_BASE
      : import.meta.env.VITE_AGENTS_BASE_URL || BETA_BASE;
  return { invokeUrl: `${base}/agents` };
}

export const agentConfig: AgentConfig = buildConfig(stage);

/**
 * Guest chat endpoints (tiers T5/T7-T8, Beta-gated by GUEST_CHAT_ENABLED).
 * `startUrl` mints the reCAPTCHA-gated 24h guest token; `invokeUrl` is the
 * streaming Lambda Function URL proxy (API Gateway buffers, Function URLs
 * stream) published at deploy time to SSM `/oculusmind/<stage>/guest/
 * invoke-url`. Baked per stage — `prod-never-calls-beta` holds: the prod
 * branch never references a beta constant.
 */
export interface GuestConfig {
  readonly startUrl: string;
  readonly invokeUrl: string;
}

const GUEST_INVOKE_BETA = 'https://y3nqggkf6w5tedpj6tqxyflcfe0prbwc.lambda-url.us-west-2.on.aws/';
const GUEST_INVOKE_PROD = 'https://i2oaywch3whdfgo5mbbzxggb2m0bzlcd.lambda-url.us-west-2.on.aws/';

function buildGuestConfig(s: 'local' | 'beta' | 'prod'): GuestConfig {
  if (s === 'prod') {
    return { startUrl: `${PROD_BASE}/guest/start`, invokeUrl: GUEST_INVOKE_PROD };
  }
  return { startUrl: `${BETA_BASE}/guest/start`, invokeUrl: GUEST_INVOKE_BETA };
}

export const guestConfig: GuestConfig = buildGuestConfig(stage);

/**
 * Canonical agent exposed over the A2A protocol. Each A2A agent has its own
 * public path `${base}/a2a/<agentId>` — the gateway mounts one child resource
 * per agent and each A2A runtime serves exactly one agent (see the agents
 * repo's `docs/a2a.md`). `uno` is the only A2A agent today; add more here as
 * their A2A runtimes ship.
 */
export const A2A_AGENT_ID = 'uno';
